import { useEffect, useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { intlLocale } from '@/i18n/dateFormat'
import { useDispatch } from 'react-redux'
import { Check } from 'lucide-react'
import { cn } from '@/lib/utils'
import type { AppDispatch } from '@/store'
import { removeAnnouncement } from '@/store/slices/announcementsSlice'
import { announcementApi } from '@/api/announcementApi'
import { publishAnnouncementEvent } from './announcementBroadcast'
import { useUserTimezone } from '@/hooks/useUserTimezone'
import { priorityStyle } from './priorityStyles'
import type { AnnouncementMineItem } from './types'
import type { SnowflakeId } from '@/types/snowflake'

const PAGE = 20

type Filter = 'all' | 'unread' | 'read'
const FILTERS: Filter[] = ['all', 'unread', 'read']

function pick(base: string | null | undefined, i18n: Record<string, string> | undefined, lng: string): string {
  return (i18n && i18n[lng]) || base || ''
}

function fmtTime(iso: string | null | undefined, tz: string, locale: string): string {
  if (!iso) return ''
  try {
    return new Date(iso).toLocaleString(intlLocale(locale), {
      timeZone: tz, year: 'numeric', month: '2-digit', day: '2-digit',
      hour: '2-digit', minute: '2-digit',
    })
  } catch {
    return ''
  }
}

interface Props {
  /**
   * 打开某条公告的详情。详情壳由宿主决定 —— 历史页用自己的 Dialog，收件箱页签
   * 走全局 <AnnouncementsHost/>（列表在 Popover 里，Dialog 不能挂在同一棵树上）。
   * 调用之前本组件已经做掉了「标记已读 + 角标同步」。
   */
  onOpenDetail: (id: SnowflakeId) => void
  className?: string
}

/**
 * 「我的公告」列表本体：筛选（全部/未读/已读）+ 分页 + 点开即已读。
 *
 * 同时服务两个宿主 —— /announcements 历史页，和通知收件箱的 Announcements 页签
 * （R-044 把顶栏独立的 Megaphone 入口并了进去，与 mobile 一致）。数据是挂载时拉
 * 一次 `/mine`：收件箱只在用户真的切到这个页签时才挂载它，所以打开铃铛本身不会
 * 多发请求。
 */
export function AnnouncementList({ onOpenDetail, className }: Props) {
  const { t, i18n } = useTranslation('announcements')
  const lng = i18n.language?.split('-')[0] ?? 'en'
  const tz = useUserTimezone()
  const dispatch = useDispatch<AppDispatch>()
  const [items, setItems] = useState<AnnouncementMineItem[]>([])
  const [loading, setLoading] = useState(true)
  const [filter, setFilter] = useState<Filter>('all')
  const [shown, setShown] = useState(PAGE)

  useEffect(() => {
    let alive = true
    setLoading(true)
    announcementApi.mine()
      .then((res) => { if (alive) setItems(Array.isArray(res) ? res : []) })
      // 后端挂了要落到空态而不是永久 loading —— 白屏 spinner 比「暂无公告」更糟。
      .catch(() => { if (alive) setItems([]) })
      .finally(() => { if (alive) setLoading(false) })
    return () => { alive = false }
  }, [])

  const filtered = useMemo(() => items.filter((a) => {
    if (filter === 'unread') return !a.isRead
    if (filter === 'read') return a.isRead
    return true
  }), [items, filter])

  const visible = filtered.slice(0, shown)

  const open = (a: AnnouncementMineItem) => {
    if (!a.isRead) {
      setItems((prev) => prev.map((x) => (x.id === a.id ? { ...x, isRead: true } : x)))
      void announcementApi.markRead(a.id)
      // 同步顶栏角标(slice)+跨标签：在列表里读掉后角标即时减，不残留。
      dispatch(removeAnnouncement(a.id))
      publishAnnouncementEvent({ kind: 'refetch' })
    }
    onOpenDetail(a.id)
  }

  return (
    <div className={className}>
      <div className="mb-3 flex gap-1">
        {FILTERS.map((f) => (
          <button
            key={f}
            onClick={() => { setFilter(f); setShown(PAGE) }}
            className={cn(
              'rounded-full px-3 py-1 text-xs font-medium',
              filter === f
                ? 'bg-primary text-primary-foreground'
                : 'bg-muted text-muted-foreground hover:bg-muted/70',
            )}
          >
            {t(`historyFilter.${f}`)}
          </button>
        ))}
      </div>

      {loading ? (
        <div className="py-12 text-center text-sm text-muted-foreground">{t('loading')}</div>
      ) : visible.length === 0 ? (
        <div className="py-12 text-center text-sm text-muted-foreground">{t('empty')}</div>
      ) : (
        <>
          <ul className="space-y-2">
            {visible.map((a) => (
              <li key={String(a.id)}>
                <button
                  onClick={() => open(a)}
                  className="flex w-full items-center gap-3 rounded-xl border bg-card px-4 py-3 text-left hover:bg-muted/40"
                >
                  <span
                    className={cn(
                      'h-2 w-2 shrink-0 rounded-full',
                      a.isRead ? 'border border-border' : priorityStyle(a.priority).dot,
                    )}
                  />
                  <div className="min-w-0 flex-1">
                    <div className="flex items-center gap-2">
                      <span className={`truncate text-sm ${a.isRead ? 'font-medium text-foreground' : 'font-semibold'}`}>
                        {pick(a.title, a.titleI18n, lng)}
                      </span>
                      {a.isExpired ? (
                        <span className="rounded bg-muted px-1.5 py-0.5 text-[10px] font-medium text-muted-foreground">{t('expired')}</span>
                      ) : null}
                    </div>
                    <div className="mt-0.5 flex items-center gap-2 text-[11px] text-muted-foreground/80">
                      {a.category ? <span>{a.category}</span> : null}
                      <span>{fmtTime(a.publishedAt, tz, i18n.language)}</span>
                    </div>
                  </div>
                  {a.isRead ? (
                    <span className="flex shrink-0 items-center gap-1 rounded-full bg-emerald-50 px-2 py-0.5 text-[11px] font-medium text-emerald-600 dark:bg-emerald-950/40 dark:text-emerald-400">
                      <Check size={12} /> {t('historyFilter.read')}
                    </span>
                  ) : null}
                </button>
              </li>
            ))}
          </ul>

          {shown < filtered.length ? (
            <div className="mt-4 text-center">
              <button
                onClick={() => setShown((n) => n + PAGE)}
                className="rounded-lg border px-4 py-2 text-sm font-medium text-muted-foreground hover:bg-muted"
              >
                {t('loadMore')}
              </button>
            </div>
          ) : null}
        </>
      )}
    </div>
  )
}
